import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Avatar,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fade,
  ButtonGroup
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  Comment as CommentIcon,
  Description as DescriptionIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  Code as CodeIcon,
  Title as TitleIcon,
  FormatQuote as FormatQuoteIcon,
  InsertLink as InsertLinkIcon,
  HorizontalRule as HorizontalRuleIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatDistanceToNow } from 'date-fns';
import { alpha } from '@mui/material/styles';

// Common styles for scrollbars
const commonStyles = {
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => alpha(theme.palette.primary.main, 0.2),
      borderRadius: '20px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: theme => `${alpha(theme.palette.primary.main, 0.2)} transparent`,
    '&:hover': {
      '&::-webkit-scrollbar-thumb': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    }
  },
};

// Tip component for the empty state
const Tip = ({ title, description, icon }) => (
  <Box
    sx={{
      p: 3.5,
      pt: 4,
      pb: 4,
      borderRadius: 2,
      bgcolor: (theme) => alpha(theme.palette.background.default, 0.7),
      backdropFilter: 'blur(8px)',
      border: '1px solid',
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
      width: 200,
      height: 180,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: (theme) => `0 8px 16px ${alpha(theme.palette.common.black, 0.08)}`,
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
      }
    }}
  >
    <Box
      sx={{
        mb: 2,
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'primary.main',
        aspectRatio: '1',
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 600,
        mb: 1,
        color: 'text.primary',
      }}
    >
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        fontSize: '0.8rem',
        lineHeight: 1.5,
      }}
    >
      {description}
    </Typography>
  </Box>
);

const DocumentationPage = ({ 
  page, 
  currentUser, 
  users, 
  categories = ['Uncategorized'],
  onUpdatePage, 
  onDeletePage,
  onAddComment,
  onDeleteComment
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const newCategoryInputRef = React.useRef(null);
  
  // Extract categories from props and include current page category if needed
  const uniqueCategories = useMemo(() => {
    const allCategories = new Set(categories);
    
    // Add current page category if not already included
    if (page?.category && !categories.includes(page.category)) {
      allCategories.add(page.category);
    }
    
    // Add any new category being entered
    if (newCategory && !allCategories.has(newCategory)) {
      allCategories.add(newCategory);
    }
    
    // Add the currently edited category if it's new
    if (editCategory && !allCategories.has(editCategory)) {
      allCategories.add(editCategory);
    }
    
    return Array.from(allCategories).sort();
  }, [categories, page?.category, newCategory, editCategory]);
  
  // If no page is selected, show a message
  if (!page) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          height: '100%',
          minHeight: 0,
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          py: 4,
          px: 4,
          textAlign: 'center',
          background: (theme) => `
            radial-gradient(circle at 90% 90%, ${alpha(theme.palette.primary.main, 0.08)}, transparent 60%),
            radial-gradient(circle at 10% 10%, ${alpha(theme.palette.secondary.main, 0.08)}, transparent 60%),
            ${theme.palette.background.paper}
          `,
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            backgroundImage: (theme) => `radial-gradient(${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
            animation: 'moveBackground 20s linear infinite',
            opacity: 0.5,
            pointerEvents: 'none'
          },
          '@keyframes moveBackground': {
            '0%': {
              transform: 'translate(0, 0)'
            },
            '100%': {
              transform: 'translate(20px, 20px)'
            }
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            mb: 3,
            width: 110,
            height: 110,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            aspectRatio: '1',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '140%',
              height: '140%',
              aspectRatio: '1',
              top: '-20%',
              left: '-20%',
              background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%'
            }
          }}
        >
          <DescriptionIcon 
            sx={{ 
              fontSize: 60, 
              color: 'primary.main',
              position: 'relative',
              zIndex: 1
            }} 
          />
        </Box>
        <Typography variant="h4" sx={{ 
          mb: 2,
          fontWeight: 'bold',
          color: 'text.primary',
          position: 'relative',
          display: 'inline-block',
          fontFamily: 'StrawberrySansDisplay',
        }}>
          Welcome to Documentation
        </Typography>
        <Typography variant="body1" sx={{ 
          mb: 5,
          maxWidth: 500,
          color: 'text.secondary',
          lineHeight: 1.6
        }}>
          Select a page from the sidebar to view its contents or create a new page to add to the documentation library.
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 3,
          maxWidth: 700
        }}>
          <Tip 
            title="Documents" 
            description="Documents for easy access to information" 
            icon={<Box sx={{ fontSize: '1.2rem' }}>📝</Box>} 
          />
          <Tip 
            title="Comment" 
            description="Collaborate by adding comments" 
            icon={<Box sx={{ fontSize: '1.2rem' }}>💬</Box>} 
          />
          <Tip 
            title="Categorize" 
            description="Organize by categories for easy navigation" 
            icon={<Box sx={{ fontSize: '1.2rem' }}>📚</Box>} 
          />
        </Box>
      </Paper>
    );
  }
  
  const handleEditClick = () => {
    setEditContent(page.content || '');
    setEditTitle(page.title || '');
    setEditCategory(page.category || '');
    setIsEditing(true);
  };
  
  const handleSaveClick = () => {
    onUpdatePage({
      ...page,
      title: editTitle.trim(),
      content: editContent,
      category: editCategory.trim(),
      lastUpdatedBy: currentUser.uid,
      lastUpdatedAt: new Date()
    });
    setIsEditing(false);
  };
  
  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };
  
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleConfirmDelete = () => {
    onDeletePage(page.id);
    setDeleteConfirmOpen(false);
  };
  
  const handleAddComment = () => {
    if (!commentText.trim()) return;
    
    onAddComment(page.id, commentText.trim());
    setCommentText('');
  };
  
  const getUserDisplayName = (userId) => {
    const user = users.find(u => u.uid === userId);
    if (!user) return 'Unknown User';
    
    // Try different name fields in order of preference
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.displayName) {
      return user.displayName;
    } else if (user.name) {
      return user.name;
    } else if (user.email) {
      return user.email.split('@')[0]; // Use email username as fallback
    }
    
    return 'Unknown User';
  };
  
  const getUserAvatar = (userId) => {
    const user = users.find(u => u.uid === userId);
    if (!user) return null;
    
    if (user.photoURL) {
      return (
        <Avatar 
          src={user.photoURL} 
          alt={getUserDisplayName(userId)} 
          imgProps={{
            referrerPolicy: "no-referrer"
          }}
          sx={{
            border: '2px solid',
            borderColor: theme => alpha(theme.palette.primary.main, 0.2)
          }}
        />
      );
    }
    
    // Create initials avatar
    let initials = '';
    if (user.firstName && user.lastName) {
      initials = `${user.firstName[0]}${user.lastName[0]}`;
    } else if (user.displayName) {
      const parts = user.displayName.split(' ');
      initials = parts.length > 1 ? `${parts[0][0]}${parts[parts.length-1][0]}` : parts[0][0];
    } else if (user.name) {
      const parts = user.name.split(' ');
      initials = parts.length > 1 ? `${parts[0][0]}${parts[parts.length-1][0]}` : parts[0][0];
    } else if (user.email) {
      initials = user.email[0].toUpperCase();
    }
    
    return (
      <Avatar 
        sx={{
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          fontWeight: 600,
          border: '2px solid',
          borderColor: theme => alpha(theme.palette.primary.main, 0.2)
        }}
      >
        {initials}
      </Avatar>
    );
  };
  
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    const dateObj = date instanceof Date ? date : date.toDate();
    return formatDistanceToNow(dateObj, { addSuffix: true });
  };
  
  const formatFullDate = (date) => {
    if (!date) return 'Unknown date';
    const dateObj = date instanceof Date ? date : date.toDate();
    return dateObj.toLocaleString('en-US', { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const comments = page.comments || [];
  
  // Function to insert markdown formatting at cursor position
  const insertFormatting = (startChars, endChars, defaultText) => {
    const textField = document.querySelector('textarea');
    if (!textField) return;
    
    const start = textField.selectionStart;
    const end = textField.selectionEnd;
    const selectedText = editContent.substring(start, end);
    const beforeText = editContent.substring(0, start);
    const afterText = editContent.substring(end);
    
    const newText = selectedText 
      ? beforeText + startChars + selectedText + endChars + afterText
      : beforeText + startChars + defaultText + endChars + afterText;
    
    setEditContent(newText);
    
    // Set cursor position after update
    setTimeout(() => {
      textField.focus();
      textField.setSelectionRange(
        selectedText ? start + startChars.length : start + startChars.length,
        selectedText ? end + startChars.length : start + startChars.length + defaultText.length
      );
    }, 0);
  };
  
  // Formatting handlers
  const handleBold = () => insertFormatting('**', '**', 'bold text');
  const handleItalic = () => insertFormatting('*', '*', 'italic text');
  const handleHeading1 = () => insertFormatting('# ', '', 'Heading 1');
  const handleHeading2 = () => insertFormatting('## ', '', 'Heading 2');
  const handleHeading3 = () => insertFormatting('### ', '', 'Heading 3');
  const handleBulletList = () => {
    const textField = document.querySelector('textarea');
    if (!textField) return;
    
    const start = textField.selectionStart;
    const end = textField.selectionEnd;
    const selectedText = editContent.substring(start, end);
    const beforeText = editContent.substring(0, start);
    const afterText = editContent.substring(end);
    
    // If there's selected text, handle multiple lines
    if (selectedText) {
      const lines = selectedText.split('\n');
      const formattedLines = lines.map(line => line.trim() ? `- ${line.trim()}` : line).join('\n');
      
      // Check if we need a leading newline
      const needsLeadingNewline = beforeText.length > 0 && beforeText[beforeText.length - 1] !== '\n';
      const newText = beforeText + (needsLeadingNewline ? '\n' : '') + formattedLines + afterText;
      
      setEditContent(newText);
      
      // Set cursor position after update
      setTimeout(() => {
        textField.focus();
        textField.setSelectionRange(
          start + (needsLeadingNewline ? 1 : 0),
          start + (needsLeadingNewline ? 1 : 0) + formattedLines.length
        );
      }, 0);
    } else {
      // Single line handling
      const needsLineBreak = beforeText.length > 0 && beforeText[beforeText.length - 1] !== '\n';
      insertFormatting(needsLineBreak ? '\n- ' : '- ', '', 'List item');
    }
  };
  const handleNumberList = () => {
    const textField = document.querySelector('textarea');
    if (!textField) return;
    
    const start = textField.selectionStart;
    const end = textField.selectionEnd;
    const selectedText = editContent.substring(start, end);
    const beforeText = editContent.substring(0, start);
    const afterText = editContent.substring(end);
    
    // If there's selected text, handle multiple lines
    if (selectedText) {
      const lines = selectedText.split('\n');
      const formattedLines = lines
        .map((line, index) => line.trim() ? `${index + 1}. ${line.trim()}` : line)
        .join('\n');
      
      // Check if we need a leading newline
      const needsLeadingNewline = beforeText.length > 0 && beforeText[beforeText.length - 1] !== '\n';
      const newText = beforeText + (needsLeadingNewline ? '\n' : '') + formattedLines + afterText;
      
      setEditContent(newText);
      
      // Set cursor position after update
      setTimeout(() => {
        textField.focus();
        textField.setSelectionRange(
          start + (needsLeadingNewline ? 1 : 0),
          start + (needsLeadingNewline ? 1 : 0) + formattedLines.length
        );
      }, 0);
    } else {
      // Single line handling
      const needsLineBreak = beforeText.length > 0 && beforeText[beforeText.length - 1] !== '\n';
      insertFormatting(needsLineBreak ? '\n1. ' : '1. ', '', 'List item');
    }
  };
  const handleQuote = () => {
    // Check if we're already at the start of a line, if not add a line break
    const currPosition = document.querySelector('textarea').selectionStart;
    const textBefore = editContent.substring(0, currPosition);
    const needsLineBreak = textBefore.length > 0 && textBefore[textBefore.length - 1] !== '\n';
    insertFormatting(needsLineBreak ? '\n> ' : '> ', '', 'Blockquote');
  };
  const handleCode = () => insertFormatting('`', '`', 'code');
  const handleCodeBlock = () => {
    // Ensure code blocks have proper line breaks before and after
    const currPosition = document.querySelector('textarea').selectionStart;
    const textBefore = editContent.substring(0, currPosition);
    const needsLineBreak = textBefore.length > 0 && textBefore[textBefore.length - 1] !== '\n';
    insertFormatting(needsLineBreak ? '\n```\n' : '```\n', '\n```', 'code block');
  };
  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      const textField = document.querySelector('textarea');
      const start = textField.selectionStart;
      const end = textField.selectionEnd;
      const selectedText = editContent.substring(start, end);
      
      insertFormatting('[', `](${url})`, selectedText || 'link text');
    }
  };
  const handleImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      insertFormatting('![', `](${url})`, 'image description');
    }
  };
  const handleHorizontalRule = () => {
    // Ensure proper spacing around horizontal rules
    const currPosition = document.querySelector('textarea').selectionStart;
    const textBefore = editContent.substring(0, currPosition);
    
    // Check if we need one or two line breaks before the rule
    let prefix = '\n\n---\n\n';
    if (textBefore.length > 0) {
      const lastChar = textBefore[textBefore.length - 1];
      const secondLastChar = textBefore.length > 1 ? textBefore[textBefore.length - 2] : '';
      
      if (lastChar === '\n' && secondLastChar === '\n') {
        prefix = '---\n\n'; // Already have two line breaks
      } else if (lastChar === '\n') {
        prefix = '\n---\n\n'; // Already have one line break
      }
    }
    
    insertFormatting(prefix, '', '');
  };
  
  return (
    <Fade in={true}>
      <Paper 
        elevation={0} 
        sx={{ 
          height: '100%',
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3
        }}
      >
        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          elevation={3}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`
            }
          }}
        >
          <MenuItem onClick={() => {
            handleMenuClose();
            handleEditClick();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Edit Page</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            handleDeleteClick();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Page</ListItemText>
          </MenuItem>
        </Menu>
        
        {/* Header */}
        <Box 
          sx={{ 
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
            bgcolor: alpha('#fff', 0.05),
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: theme => `linear-gradient(90deg, 
                ${alpha(theme.palette.primary.main, 0)}, 
                ${alpha(theme.palette.primary.main, 0.2)}, 
                ${alpha(theme.palette.secondary.main, 0.2)}, 
                ${alpha(theme.palette.secondary.main, 0)}
              )`,
              opacity: 0.6
            }
          }}
        >
          <Box>
            {isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Page Title"
                  variant="outlined"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
                
                <TextField
                  select
                  label="Category"
                  variant="outlined"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: 2
                    }
                  }}
                >
                  {uniqueCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                  {!uniqueCategories.includes(editCategory) && editCategory && (
                    <MenuItem value={editCategory}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <span>"{editCategory}"</span>
                        <Chip 
                          label="New" 
                          size="small" 
                          color="primary" 
                          sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} 
                        />
                      </Box>
                    </MenuItem>
                  )}
                  <MenuItem value="Uncategorized">Uncategorized</MenuItem>
                </TextField>
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Create a new category
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Enter new category name..."
                      variant="outlined"
                      inputRef={newCategoryInputRef}
                      InputProps={{
                        sx: {
                          borderRadius: 2
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          setNewCategory(e.target.value.trim());
                          setEditCategory(e.target.value.trim());
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        if (newCategoryInputRef.current && newCategoryInputRef.current.value.trim()) {
                          const categoryName = newCategoryInputRef.current.value.trim();
                          setNewCategory(categoryName);
                          setEditCategory(categoryName);
                          newCategoryInputRef.current.value = '';
                        }
                      }}
                      sx={{ flexShrink: 0, borderRadius: 2 }}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                    fontWeight: 500,
                    color: 'text.primary',
                    fontFamily: 'StrawberrySansDisplay'
                  }}
                >
                  {page.title}
                </Typography>
                
                <Stack direction="row" spacing={2} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Created By:
                    </Typography>
                    {page.createdByUser && page.createdByUser.avatar ? (
                      <Tooltip title={`${page.createdByUser.name} - Created on ${formatFullDate(page.createdAt)}`}>
                        <Avatar 
                          src={page.createdByUser.avatar} 
                          alt={page.createdByUser.name}
                          imgProps={{
                            referrerPolicy: "no-referrer"
                          }}
                          sx={{
                            border: '2px solid',
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            width: 28,
                            height: 28
                          }}
                        />
                      </Tooltip>
                    ) : (
                      getUserAvatar(page.createdBy) ? (
                        <Tooltip title={`${getUserDisplayName(page.createdBy)} - Created on ${formatFullDate(page.createdAt)}`}>
                          {getUserAvatar(page.createdBy)}
                        </Tooltip>
                      ) : (
                        getUserAvatar(page.createdBy)
                      )
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(page.createdAt)}
                    </Typography>
                  </Stack>
                  
                  {page.category && (
                    <Chip 
                      label={page.category} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{
                        borderRadius: '12px',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24
                      }}
                    />
                  )}
                  
                  {page.lastUpdatedAt && page.lastUpdatedAt.toString() !== page.createdAt.toString() && (
                    <Tooltip 
                      title={`Last updated ${formatFullDate(page.lastUpdatedAt)} by ${
                        page.lastUpdatedByUser?.name || getUserDisplayName(page.lastUpdatedBy)
                      }`}
                    >
                      <Chip 
                        label="Updated" 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                        sx={{
                          borderRadius: '12px',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </>
            )}
          </Box>
          
          {!isEditing && (
            <Box>
              <IconButton 
                onClick={handleMenuOpen} 
                aria-label="page actions"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          )}
        </Box>
        
        {/* Content */}
        <Box 
          sx={{ 
            p: 3, 
            flex: 1, 
            overflow: 'auto',
            ...commonStyles.scrollbar
          }}
        >
          {isEditing ? (
            <>
              <Box 
                sx={{ 
                  mb: 2, 
                  p: 1, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                  overflowX: 'auto'
                }}
              >
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <ButtonGroup size="small" variant="outlined">
                    <Tooltip title="Bold (Ctrl+B)">
                      <Button onClick={handleBold}><FormatBoldIcon fontSize="small" /></Button>
                    </Tooltip>
                    <Tooltip title="Italic (Ctrl+I)">
                      <Button onClick={handleItalic}><FormatItalicIcon fontSize="small" /></Button>
                    </Tooltip>
                    <Tooltip title="Link">
                      <Button onClick={handleLink}><InsertLinkIcon fontSize="small" /></Button>
                    </Tooltip>
                  </ButtonGroup>
                  
                  <ButtonGroup size="small" variant="outlined">
                    <Tooltip title="Heading 1">
                      <Button onClick={handleHeading1}><TitleIcon fontSize="small" /></Button>
                    </Tooltip>
                    <Tooltip title="Heading 2">
                      <Button onClick={handleHeading2}>
                        <Typography variant="button" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>H2</Typography>
                      </Button>
                    </Tooltip>
                    <Tooltip title="Heading 3">
                      <Button onClick={handleHeading3}>
                        <Typography variant="button" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>H3</Typography>
                      </Button>
                    </Tooltip>
                  </ButtonGroup>
                  
                  <ButtonGroup size="small" variant="outlined">
                    <Tooltip title="Bullet List">
                      <Button onClick={handleBulletList}><FormatListBulletedIcon fontSize="small" /></Button>
                    </Tooltip>
                    <Tooltip title="Numbered List">
                      <Button onClick={handleNumberList}><FormatListNumberedIcon fontSize="small" /></Button>
                    </Tooltip>
                    <Tooltip title="Quote">
                      <Button onClick={handleQuote}><FormatQuoteIcon fontSize="small" /></Button>
                    </Tooltip>
                  </ButtonGroup>
                  
                  <ButtonGroup size="small" variant="outlined">
                    <Tooltip title="Inline Code">
                      <Button onClick={handleCode}><CodeIcon fontSize="small" /></Button>
                    </Tooltip>
                    <Tooltip title="Code Block">
                      <Button onClick={handleCodeBlock}>
                        <Typography variant="button" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '0.75rem' }}>{ '{ }' }</Typography>
                      </Button>
                    </Tooltip>
                  </ButtonGroup>
                  
                  <ButtonGroup size="small" variant="outlined">
                    <Tooltip title="Image">
                      <Button onClick={handleImage}><ImageIcon fontSize="small" /></Button>
                    </Tooltip>
                    <Tooltip title="Horizontal Rule">
                      <Button onClick={handleHorizontalRule}><HorizontalRuleIcon fontSize="small" /></Button>
                    </Tooltip>
                  </ButtonGroup>
                </Stack>
              </Box>
              <TextField
                multiline
                fullWidth
                minRows={20}
                placeholder="Write your documentation content here... (Markdown is supported)"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                sx={{ mb: 2 }}
                variant="outlined"
              />
            </>
          ) : (
            <Box 
              sx={{ 
                typography: 'body1',
                whiteSpace: 'pre-line',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 500,
                  color: 'text.primary',
                  mt: 0.75,
                  mb: 0,
                  lineHeight: 1.2
                },
                '& h1': { 
                  fontSize: '2rem', 
                  borderBottom: '1px solid', 
                  borderColor: alpha('#888', 0.1), 
                  pb: 0.25,
                  '& + p, & + ul, & + ol': { mt: 0.5 }
                },
                '& h2': { 
                  fontSize: '1.75rem',
                  '& + p, & + ul, & + ol': { mt: 0.5 }
                },
                '& h3': { 
                  fontSize: '1.5rem',
                  '& + p, & + ul, & + ol': { mt: 0.5 }
                },
                '& h4': { 
                  fontSize: '1.25rem',
                  '& + p, & + ul, & + ol': { mt: 0.5 }
                },
                '& h5': { 
                  fontSize: '1.1rem',
                  '& + p, & + ul, & + ol': { mt: 0.5 }
                },
                '& h6': { 
                  fontSize: '1rem',
                  '& + p, & + ul, & + ol': { mt: 0.5 }
                },
                '& p': { mb: 0.5, lineHeight: 1.4 },
                '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                '& blockquote': { 
                  borderLeft: '4px solid', 
                  borderColor: alpha('#888', 0.2), 
                  my: 0.5, 
                  pl: 2,
                  color: 'text.secondary'
                },
                '& ul, & ol': { mb: 0.75, pl: 3 },
                '& li': { mb: 0.25, pl: 1 },
                '& li > p': { mb: 0.25 },
                '& ul li': { 
                  position: 'relative',
                  pl: 1
                },
                '& ol li': { 
                  position: 'relative',
                  pl: 1 
                },
                '& br': { 
                  display: 'block',
                  content: '""',
                  marginTop: '0.2em' 
                },
                '& table': { 
                  width: '100%', 
                  borderCollapse: 'collapse', 
                  mb: 2,
                  border: '1px solid',
                  borderColor: alpha('#888', 0.1)
                },
                '& th': { 
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                  textAlign: 'left',
                  p: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#888', 0.1)
                },
                '& td': { 
                  p: 1.5, 
                  border: '1px solid',
                  borderColor: alpha('#888', 0.1)
                },
                '& code': {
                  fontFamily: 'monospace',
                  bgcolor: alpha('#888', 0.1),
                  p: 0.5,
                  borderRadius: 1,
                  fontSize: '0.9em'
                },
                '& pre': {
                  bgcolor: theme => alpha(theme.palette.background.default, 0.5),
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: alpha('#888', 0.1),
                  '& code': {
                    bgcolor: 'transparent',
                    p: 0
                  }
                }
              }}
            >
              {page.content ? (
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]} 
                  breaks={true}
                  components={{
                    // Add custom components to handle certain elements better
                    p: ({node, children, ...props}) => <p style={{marginBottom: '0.5em'}} {...props}>{children}</p>,
                    li: ({node, children, ...props}) => <li style={{marginBottom: '0.15em'}} {...props}>{children}</li>,
                    br: ({node, ...props}) => <br style={{display: 'block', marginTop: '0.15em'}} {...props} />,
                    h1: ({node, children, ...props}) => <h1 style={{marginTop: '0.75em', marginBottom: '0', paddingBottom: '0.25em'}} {...props}>{children}</h1>,
                    h2: ({node, children, ...props}) => <h2 style={{marginTop: '0.75em', marginBottom: '0'}} {...props}>{children}</h2>,
                    h3: ({node, children, ...props}) => <h3 style={{marginTop: '0.75em', marginBottom: '0'}} {...props}>{children}</h3>,
                    h4: ({node, children, ...props}) => <h4 style={{marginTop: '0.75em', marginBottom: '0'}} {...props}>{children}</h4>,
                    h5: ({node, children, ...props}) => <h5 style={{marginTop: '0.75em', marginBottom: '0'}} {...props}>{children}</h5>,
                    h6: ({node, children, ...props}) => <h6 style={{marginTop: '0.75em', marginBottom: '0'}} {...props}>{children}</h6>
                  }}
                >
                  {page.content}
                </ReactMarkdown>
              ) : (
                <Box 
                  sx={{ 
                    py: 4, 
                    textAlign: 'center', 
                    bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    my: 2
                  }}
                >
                  <Typography color="text.secondary" sx={{ mb: 1 }}>
                    This page has no content yet.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                  >
                    Add Content
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
        
        {/* Editing actions */}
        {isEditing && (
          <Box sx={{ 
            p: 2, 
            borderTop: 1, 
            borderColor: 'divider', 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            bgcolor: theme => alpha(theme.palette.background.default, 0.5)
          }}>
            <Button 
              variant="outlined" 
              startIcon={<CancelIcon />} 
              onClick={() => setIsEditing(false)}
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              startIcon={<SaveIcon />} 
              onClick={handleSaveClick}
              disabled={!editTitle.trim()}
              color="primary"
            >
              Save Changes
            </Button>
          </Box>
        )}
        
        {/* Comments section */}
        {!isEditing && page && (
          <Box sx={{ 
            borderTop: 1, 
            borderColor: 'divider', 
            p: 3,
            bgcolor: theme => alpha(theme.palette.background.default, 0.3)
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              mb: 2
            }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CommentIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 500,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.75rem'
                }}
              >
                Comments ({comments.length})
              </Typography>
            </Box>
            
            {comments.length > 0 && (
              <Box sx={{ mb: 3, maxHeight: 400, overflow: 'auto', ...commonStyles.scrollbar }}>
                {comments.map((comment, index) => (
                  <Paper 
                    key={index} 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      borderRadius: 2, 
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme => alpha(theme.palette.primary.main, 0.3),
                        boxShadow: theme => `0 3px 8px ${alpha(theme.palette.common.black, 0.05)}`
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>
                      {comment.user && comment.user.avatar ? (
                        <Avatar 
                          src={comment.user.avatar} 
                          alt={comment.user.name || getUserDisplayName(comment.createdBy)}
                          imgProps={{
                            referrerPolicy: "no-referrer"
                          }}
                          sx={{
                            border: '2px solid',
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            width: 28,
                            height: 28
                          }}
                        />
                      ) : (
                        getUserAvatar(comment.createdBy)
                      )}
                      <Box>
                        <Typography 
                          variant="subtitle2"
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.875rem', 
                            lineHeight: 1.2 
                          }}
                        >
                          {comment.user?.name || getUserDisplayName(comment.createdBy)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {formatDate(comment.createdAt)}
                        </Typography>
                      </Box>
                      
                      {comment.createdBy === currentUser.uid && (
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={() => onDeleteComment(page.id, index)}
                          sx={{ 
                            ml: 'auto',
                            opacity: 0.5,
                            '&:hover': {
                              opacity: 1,
                              bgcolor: theme => alpha(theme.palette.error.main, 0.08)
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ pl: 0.5 }}>{comment.text}</Typography>
                  </Paper>
                ))}
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <Button 
                variant="contained" 
                disabled={!commentText.trim()} 
                onClick={handleAddComment}
                sx={{
                  borderRadius: 2,
                  px: 3
                }}
              >
                Post
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`
            }
          }}
        >
          <DialogTitle>
            Delete Documentation Page
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{page.title}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button 
              onClick={() => setDeleteConfirmOpen(false)}
              variant="outlined"
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Fade>
  );
};

export default DocumentationPage; 